/**
 * Viewport geometry: the arithmetic that turns scroll offsets into a range of
 * rows and columns (Requirements 1.4, 1.6, 1.7).
 *
 * Everything in this module is pure and free of DOM references, which is what
 * lets Property 4 test it directly.
 *
 * **Why an axis model rather than a prefix-sum array.** A sheet has 5,000,000
 * rows. A cumulative-offset array over them costs 40 MB per sheet and has to be
 * rebuilt whenever one row is resized, so it is out. But the *overrides* are
 * sparse — the Data_Store stores only the rows whose height differs from the
 * default — so an axis is modelled as
 *
 * ```
 * offset(i) = i * defaultPx + Σ (px(j) - defaultPx) for every override j < i
 * ```
 *
 * With the override indices sorted and their deltas prefix-summed, `offsetOf` and
 * `indexAt` are both a binary search over the override count, which is tens of
 * entries in practice and never row count. A sheet with no resizes degenerates to
 * multiplication and division, which is the common case for a 5M-row import.
 *
 * **Why the buffer is split across both sides.** Requirement 1.4 caps the buffer
 * at "50 rows and 20 columns" *surrounding* the viewport, so the painted range may
 * exceed the visible region by 50 rows in total, not 50 per edge. The default is
 * therefore 25 rows and 10 columns per side, and {@link computeVisibleRange}
 * clamps rather than redistributes at the sheet edges: scrolling to row 0 does not
 * license 50 rows of buffer below.
 */

import { MAX_COLS, MAX_ROWS, type Range, type SheetId } from '../core/types.js';

/** Total buffer rows the render range may add beyond the visible rows (R1.4). */
export const MAX_BUFFER_ROWS = 50;

/** Total buffer columns the render range may add beyond the visible ones (R1.4). */
export const MAX_BUFFER_COLS = 20;

/** Per-side defaults, chosen so both sides together stay inside the caps. */
export const DEFAULT_BUFFER_ROWS = MAX_BUFFER_ROWS / 2;
export const DEFAULT_BUFFER_COLS = MAX_BUFFER_COLS / 2;

/** Width of the row-header gutter in CSS pixels. */
export const ROW_HEADER_WIDTH_PX = 56;

/** Height of the column-header band in CSS pixels. */
export const COL_HEADER_HEIGHT_PX = 22;

/**
 * Collapsed index ranges: the rows a filter hides (Requirement 7.4).
 *
 * Runs rather than one entry per hidden index, for the same reason the axis stores
 * sparse overrides: a filter over five million rows hides stretches, and a per-row
 * structure would cost more than the grid it describes. `before[n]` is the pixel
 * extent hidden by the first `n` runs, so a hidden-aware offset is still one binary
 * search and one subtraction.
 */
export interface HiddenRuns {
  /** Run starts, ascending, merged so no two runs touch. */
  readonly starts: Int32Array;
  /** Run ends, inclusive, parallel to {@link HiddenRuns.starts}. */
  readonly ends: Int32Array;
  /** Pixels hidden by the first `n` runs. */
  readonly before: Float64Array;
}

const NO_HIDDEN: HiddenRuns = {
  starts: new Int32Array(0),
  ends: new Int32Array(0),
  before: new Float64Array(1),
};

/** One axis of the grid: uniform default size, sparse overrides, hidden runs. */
export interface Axis {
  /** Size in CSS pixels of an index with no override. */
  defaultPx: number;
  /** Number of addressable indices (rows or columns). */
  count: number;
  /** Override indices, ascending. */
  readonly indices: Int32Array;
  /** Override sizes, parallel to {@link Axis.indices}. */
  readonly sizes: Float64Array;
  /**
   * `deltaBefore[n]` is the total `px - defaultPx` of the first `n` overrides, so
   * `offsetOf(i)` needs one binary search and one addition.
   */
  readonly deltaBefore: Float64Array;
  /** Collapsed indices. Empty on a sheet with no active filter. */
  readonly hidden: HiddenRuns;
}

/** Sorts, clamps, and merges hidden runs so no two of them touch. */
function normalizeRuns(
  runs: readonly (readonly [number, number])[],
  count: number,
): [number, number][] {
  const clamped: [number, number][] = [];
  for (const [start, end] of runs) {
    const lo = Math.max(0, Math.min(start, end));
    const hi = Math.min(count - 1, Math.max(start, end));
    if (hi < lo) continue;
    clamped.push([lo, hi]);
  }
  clamped.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const run of clamped) {
    const last = merged[merged.length - 1];
    // Adjacent runs are merged too, so skipping a hidden index never lands inside
    // another run and `nextVisible` needs exactly one jump.
    if (last !== undefined && run[0] <= last[1] + 1) {
      last[1] = Math.max(last[1], run[1]);
      continue;
    }
    merged.push([run[0], run[1]]);
  }
  return merged;
}

/**
 * Builds an axis from the sparse overrides the kernel reports.
 *
 * Pairs may arrive in any order and may repeat an index; the last pair for an
 * index wins, matching `setColWidth` semantics. `hiddenRuns` collapses indices to zero
 * extent, which is how a filtered row disappears without the axis having to hold a
 * per-row size (Requirement 7.4).
 */
export function createAxis(
  defaultPx: number,
  overrides: readonly (readonly [number, number])[],
  count: number,
  hiddenRuns: readonly (readonly [number, number])[] = [],
): Axis {
  const merged = new Map<number, number>();
  for (const [index, px] of overrides) {
    if (!Number.isInteger(index) || index < 0 || index >= count) continue;
    merged.set(index, px);
  }
  const indices = [...merged.keys()].sort((a, b) => a - b);
  const sizes = new Float64Array(indices.length);
  const deltaBefore = new Float64Array(indices.length + 1);
  for (let i = 0; i < indices.length; i++) {
    const px = merged.get(indices[i] as number) as number;
    sizes[i] = px;
    deltaBefore[i + 1] = (deltaBefore[i] as number) + px - defaultPx;
  }
  const axis: Axis = {
    defaultPx,
    count,
    indices: Int32Array.from(indices),
    sizes,
    deltaBefore,
    hidden: NO_HIDDEN,
  };
  const runs = normalizeRuns(hiddenRuns, count);
  if (runs.length === 0) return axis;

  const starts = new Int32Array(runs.length);
  const ends = new Int32Array(runs.length);
  const before = new Float64Array(runs.length + 1);
  for (let i = 0; i < runs.length; i++) {
    const [start, end] = runs[i] as [number, number];
    starts[i] = start;
    ends[i] = end;
    before[i + 1] =
      (before[i] as number) + (rawOffsetOf(axis, end + 1) - rawOffsetOf(axis, start));
  }
  return { ...axis, hidden: { starts, ends, before } };
}

/** Number of overrides ahead of `index`. */
function overridesBefore(axis: Axis, index: number): number {
  let lo = 0;
  let hi = axis.indices.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if ((axis.indices[mid] as number) < index) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Offset ignoring hidden runs: the axis as it would be with nothing filtered. */
function rawOffsetOf(axis: Axis, index: number): number {
  const clamped = Math.max(0, Math.min(index, axis.count));
  return clamped * axis.defaultPx + (axis.deltaBefore[overridesBefore(axis, clamped)] as number);
}

/** Number of hidden runs that end before `index`. */
function runsBefore(axis: Axis, index: number): number {
  const { ends } = axis.hidden;
  let lo = 0;
  let hi = ends.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if ((ends[mid] as number) < index) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** True when `index` is collapsed by a hidden run (Requirement 7.4). */
export function isHidden(axis: Axis, index: number): boolean {
  const at = runsBefore(axis, index);
  if (at >= axis.hidden.starts.length) return false;
  return (axis.hidden.starts[at] as number) <= index;
}

/** The first visible index at or after `index`, or `count` when none is. */
export function nextVisible(axis: Axis, index: number): number {
  if (index >= axis.count) return axis.count;
  const at = runsBefore(axis, index);
  if (at >= axis.hidden.starts.length) return index;
  if ((axis.hidden.starts[at] as number) > index) return index;
  // Runs are merged and non-adjacent, so one jump lands on a visible index.
  return (axis.hidden.ends[at] as number) + 1 >= axis.count
    ? axis.count
    : (axis.hidden.ends[at] as number) + 1;
}

/** The last visible index at or before `index`, or `-1` when none is. */
export function prevVisible(axis: Axis, index: number): number {
  if (index < 0) return -1;
  const at = runsBefore(axis, index);
  if (at >= axis.hidden.starts.length) return index;
  const start = axis.hidden.starts[at] as number;
  if (start > index) return index;
  return start - 1;
}

/** Pixels hidden by the runs that lie before `index`, partial run included. */
function hiddenPxBefore(axis: Axis, index: number): number {
  const { starts, ends, before } = axis.hidden;
  if (starts.length === 0) return 0;
  const at = runsBefore(axis, index);
  let px = before[at] as number;
  if (at < starts.length) {
    const start = starts[at] as number;
    if (start < index && index <= (ends[at] as number)) {
      px += rawOffsetOf(axis, index) - rawOffsetOf(axis, start);
    }
  }
  return px;
}

/** Size of one index in CSS pixels. Zero for a hidden index. */
export function sizeOf(axis: Axis, index: number): number {
  if (axis.hidden.starts.length > 0 && isHidden(axis, index)) return 0;
  const at = overridesBefore(axis, index);
  if (at < axis.indices.length && axis.indices[at] === index) return axis.sizes[at] as number;
  return axis.defaultPx;
}

/**
 * Pixel offset of the leading edge of `index` from the start of the axis, hidden
 * indices contributing nothing.
 *
 * Non-decreasing in `index` — a hidden index shares its offset with the next visible
 * one — which is what keeps {@link indexAt}'s binary search valid.
 */
export function offsetOf(axis: Axis, index: number): number {
  const clamped = Math.max(0, Math.min(index, axis.count));
  const raw = rawOffsetOf(axis, clamped);
  return axis.hidden.starts.length === 0 ? raw : raw - hiddenPxBefore(axis, clamped);
}

/**
 * Visible index spans inside `[from, to]`, in ascending order (Requirement 7.4).
 *
 * The Grid_Renderer and the tile cache both need this: a viewport whose top and bottom
 * rows sit either side of a million hidden rows spans a million *indices* but only a
 * screenful of visible ones, and neither painting nor tile fetching may be proportional
 * to the gap.
 */
export function visibleSegments(axis: Axis, from: number, to: number): [number, number][] {
  const lo = Math.max(0, from);
  const hi = Math.min(axis.count - 1, to);
  if (hi < lo) return [];
  if (axis.hidden.starts.length === 0) return [[lo, hi]];
  const out: [number, number][] = [];
  let at = lo;
  while (at <= hi) {
    const start = nextVisible(axis, at);
    if (start > hi) break;
    const run = runsBefore(axis, start + 1);
    const nextHidden =
      run < axis.hidden.starts.length ? (axis.hidden.starts[run] as number) : axis.count;
    const end = Math.min(hi, nextHidden - 1);
    out.push([start, end]);
    at = end + 1;
  }
  return out;
}

/** Total pixel extent of the axis. */
export function totalPx(axis: Axis): number {
  return offsetOf(axis, axis.count);
}

/**
 * The index whose band contains `px`, clamped into the axis.
 *
 * Monotonic in `px` by construction, because it binary-searches `offsetOf`, which
 * is monotonic: no zero-size band can make the search skip an index it should
 * have returned.
 */
export function indexAt(axis: Axis, px: number): number {
  if (axis.count === 0) return 0;
  if (px <= 0) return visibleNear(axis, 0);
  if (px >= totalPx(axis)) return visibleNear(axis, axis.count - 1);
  let lo = 0;
  let hi = axis.count - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1;
    if (offsetOf(axis, mid) <= px) lo = mid;
    else hi = mid - 1;
  }
  return visibleNear(axis, lo);
}

/**
 * `index` itself when it is visible, otherwise the nearest visible index.
 *
 * A hidden index shares its offset with its neighbours, so a pixel lookup can land on
 * one; returning it would hand the caller a row the user cannot see, and every caller —
 * selection, hit testing, the editor — would have to correct for it.
 */
function visibleNear(axis: Axis, index: number): number {
  if (axis.hidden.starts.length === 0) return index;
  const after = nextVisible(axis, index);
  if (after < axis.count) return after;
  const before = prevVisible(axis, index);
  return before < 0 ? index : before;
}

/** What the user can currently see, in CSS pixels of the scrolling pane. */
export interface Viewport {
  scrollX: number;
  scrollY: number;
  /** Width of the scrolling pane, headers excluded. */
  widthPx: number;
  /** Height of the scrolling pane, headers excluded. */
  heightPx: number;
}

/** Buffer sizes per side. Both sides together must stay inside the R1.4 caps. */
export interface BufferSpec {
  rows: number;
  cols: number;
}

/** A visible region and the buffered region actually painted. */
export interface VisibleRange {
  /** Rows and columns that intersect the viewport rectangle. */
  visible: Range;
  /** {@link VisibleRange.visible} grown by the buffer and clamped to the sheet. */
  render: Range;
}

function clampSpan(from: number, to: number, count: number): [number, number] {
  const lo = Math.max(0, Math.min(from, count - 1));
  const hi = Math.max(lo, Math.min(to, count - 1));
  return [lo, hi];
}

/**
 * The rows and columns to paint for a viewport (Requirement 1.4, Property 4).
 *
 * `visible` is exactly the set of indices whose band intersects the viewport
 * rectangle — the last partially visible row and column included, since a half
 * row at the bottom edge still has to be drawn. `render` adds the buffer and
 * clamps to the sheet, so it never exceeds `visible` by more than
 * {@link MAX_BUFFER_ROWS} rows or {@link MAX_BUFFER_COLS} columns as long as the
 * caller's `buffer` respects those caps.
 */
export function computeVisibleRange(
  sheet: SheetId,
  rowAxis: Axis,
  colAxis: Axis,
  viewport: Viewport,
  buffer: BufferSpec = { rows: DEFAULT_BUFFER_ROWS, cols: DEFAULT_BUFFER_COLS },
): VisibleRange {
  const top = Math.max(0, viewport.scrollY);
  const left = Math.max(0, viewport.scrollX);
  // A zero-height pane still shows the row under the scroll offset: it is the row
  // the keyboard and the editor act on, so it is never excluded.
  const bottom = top + Math.max(0, viewport.heightPx);
  const right = left + Math.max(0, viewport.widthPx);

  const [r0, r1] = clampSpan(indexAt(rowAxis, top), indexAt(rowAxis, bottom), rowAxis.count);
  const [c0, c1] = clampSpan(indexAt(colAxis, left), indexAt(colAxis, right), colAxis.count);

  const bufRows = Math.max(0, Math.floor(buffer.rows));
  const bufCols = Math.max(0, Math.floor(buffer.cols));
  const [br0, br1] = clampSpan(r0 - bufRows, r1 + bufRows, rowAxis.count);
  const [bc0, bc1] = clampSpan(c0 - bufCols, c1 + bufCols, colAxis.count);

  return {
    visible: { sheet, r0, c0, r1, c1 },
    render: { sheet, r0: br0, c0: bc0, r1: br1, c1: bc1 },
  };
}

/** Largest scroll offsets that keep content in view. */
export function maxScroll(
  rowAxis: Axis,
  colAxis: Axis,
  viewport: { widthPx: number; heightPx: number },
): { x: number; y: number } {
  return {
    x: Math.max(0, totalPx(colAxis) - Math.max(0, viewport.widthPx)),
    y: Math.max(0, totalPx(rowAxis) - Math.max(0, viewport.heightPx)),
  };
}

/** Clamps a scroll offset pair into `[0, maxScroll]`. */
export function clampScroll(
  scroll: { x: number; y: number },
  rowAxis: Axis,
  colAxis: Axis,
  viewport: { widthPx: number; heightPx: number },
): { x: number; y: number } {
  const max = maxScroll(rowAxis, colAxis, viewport);
  return {
    x: Math.min(Math.max(0, scroll.x), max.x),
    y: Math.min(Math.max(0, scroll.y), max.y),
  };
}

/**
 * The scroll offsets that bring `row`/`col` fully into view, moving as little as
 * possible. Used by keyboard navigation and by "go to" from the name box.
 */
export function scrollToShow(
  scroll: { x: number; y: number },
  rowAxis: Axis,
  colAxis: Axis,
  viewport: { widthPx: number; heightPx: number },
  row: number,
  col: number,
): { x: number; y: number } {
  let { x, y } = scroll;
  const rowTop = offsetOf(rowAxis, row);
  const rowBottom = rowTop + sizeOf(rowAxis, row);
  if (rowTop < y) y = rowTop;
  else if (rowBottom > y + viewport.heightPx) y = rowBottom - viewport.heightPx;

  const colLeft = offsetOf(colAxis, col);
  const colRight = colLeft + sizeOf(colAxis, col);
  if (colLeft < x) x = colLeft;
  else if (colRight > x + viewport.widthPx) x = colRight - viewport.widthPx;

  return clampScroll({ x, y }, rowAxis, colAxis, viewport);
}

/**
 * The row axis of a sheet, from the kernel's `SheetView` payload.
 *
 * Filtered rows arrive as hidden runs and become zero-extent bands, so the whole of
 * "hidden rows do not take up space" is expressed in the axis: scroll extent, hit
 * testing, the editor overlay, and the painter all follow from it (Requirement 7.4).
 */
export function rowAxisOf(view: {
  defaultRowHeightPx: number;
  rowHeights: readonly (readonly [number, number])[];
  hiddenRows?: readonly (readonly [number, number])[];
}): Axis {
  return createAxis(view.defaultRowHeightPx, view.rowHeights, MAX_ROWS, view.hiddenRows ?? []);
}

/** The column axis of a sheet, from the kernel's `SheetView` payload. */
export function colAxisOf(view: {
  defaultColWidthPx: number;
  colWidths: readonly (readonly [number, number])[];
}): Axis {
  return createAxis(view.defaultColWidthPx, view.colWidths, MAX_COLS);
}
